/**
 * «Fiado»: everything the clients owe, in one list (the owner, 2026-10-07: «instead of the prices
 * button can we get a "Credit" button that shows all outstanding receivables (credit given out)»).
 *
 * What each one owes, and how old it is, is «Análisis»' own (`cuentasConFiado`,
 * `fiadoPorAntiguedad`), so the two screens say the same. This adds what somebody going after it
 * wants beside each name: the limit they were given, and when they last paid anything. Pure, but
 * for `leerFiado`.
 */

import type { Centavos } from '@abastece/shared';
import type { Abono, Cliente } from '@abastece/core';
import { db } from '../db/dexie.js';
import { cuentasConFiado, fiadoPorAntiguedad, type FiadoPorAntiguedad } from './analisis.js';
import { coincideTelefono } from './telefono.js';
import { coincide } from './texto.js';

export interface Deudor {
  clienteId: string;
  nombre: string;
  telefono: string | null;
  /** A client taken off the list still owes what they owe. */
  activo: boolean;
  saldo: Centavos;
  /** How many days ago the oldest of it was given. */
  dias: number;
  /** The most they may owe without the owner; null when every fiado takes the owner. */
  limite: Centavos | null;
  /** The day of the last payment of theirs that stands; null when they never paid anything. */
  ultimoAbono: string | null;
}

/** Who owes, as `fiadoPorAntiguedad` lists them, with their limit and their last payment. */
export function deudoresDe(fiado: FiadoPorAntiguedad, clientes: readonly Cliente[], abonos: readonly Abono[]): Deudor[] {
  const porId = new Map(clientes.map((c) => [c.id, c]));
  const ultimo = new Map<string, string>();
  for (const a of abonos) if (!a.anulado && a.fecha > (ultimo.get(a.clienteId) ?? '')) ultimo.set(a.clienteId, a.fecha);
  return fiado.clientes.map((d) => {
    const c = porId.get(d.clienteId);
    return {
      clienteId: d.clienteId,
      nombre: d.nombre,
      telefono: c?.telefono ?? null,
      activo: c?.activo ?? true,
      saldo: d.saldo,
      dias: d.dias,
      limite: c?.limiteCredito ?? null,
      ultimoAbono: ultimo.get(d.clienteId) ?? null,
    };
  });
}

/** The oldest debt first, as «Análisis» lists them; or the most owed first. */
export type OrdenFiado = 'antiguo' | 'monto';

/** The ones a search finds, by name or by phone, in the order asked for; the name last of all. */
export function ordenarDeudores(lista: readonly Deudor[], orden: OrdenFiado, consulta: string): Deudor[] {
  const q = consulta.trim();
  const porNombre = (a: Deudor, b: Deudor) => a.nombre.localeCompare(b.nombre, 'es');
  return lista
    .filter((d) => !q || coincide(d.nombre, q) || coincideTelefono(d.telefono, q))
    .sort((a, b) =>
      orden === 'monto' ? b.saldo - a.saldo || b.dias - a.dias || porNombre(a, b) : b.dias - a.dias || b.saldo - a.saldo || porNombre(a, b),
    );
}

/** Owing more than the limit they were given: the owner let it past, or the limit came down since. */
export const pasoSuLimite = (d: Pick<Deudor, 'saldo' | 'limite'>): boolean => d.limite !== null && d.saldo > d.limite;

/** What the clients owe, from the phone's books: only the accounts of those who owe are read. */
export async function leerFiado(hoy: string): Promise<{ fiado: FiadoPorAntiguedad; deudores: Deudor[] }> {
  const clientes = await db.clientes.toArray();
  const deben = clientes.filter((c) => (c.saldo ?? 0) > 0);
  const ids = deben.map((c) => c.id);
  const [ventas, abonos, devoluciones, incobrables] = await Promise.all([
    db.ventas.where('clienteId').anyOf(ids).toArray(),
    db.abonos.where('clienteId').anyOf(ids).toArray(),
    db.devoluciones.where('clienteId').anyOf(ids).toArray(),
    db.incobrables.where('clienteId').anyOf(ids).toArray(),
  ]);
  const fiado = fiadoPorAntiguedad(cuentasConFiado({ deudores: deben, ventas, abonos, devoluciones, incobrables }), hoy);
  return { fiado, deudores: deudoresDe(fiado, clientes, abonos) };
}
